(ns frontend.view.grid.fund-commitments-grid.view
  (:require
   [frontend.routing :as routing]
   [potpuri.core :as potpuri]
   [frontend.view.grid.fund-commitments-grid.events :as events]
   [frontend.view.grid.fund-commitments-grid.db :as db]
   [frontend.ux-overhaul.listing.fund-commitment.view :as fund-commitment-listing]
   [common.localization :as loc]
   [common.fields.table-column-widths :as widths]
   [common.schema.registry :as registry]
   [frontend.react.virtualized.scrollable-grid.view :as scrollable-grid]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.react.virtualized.scrollable-grid.kanban :as kanban]
   [frontend.react.virtualized.scrollable-grid.components :as grid-components]
   [frontend.db :as app-db]
   [frontend.utils :as fu]
   [frontend.local-storage :as ls]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.state.current-session :as session]
   [common.attribute :as attribute]
   [frontend.react.virtualized.utils :as vu]
   [frontend.view.entity :as f-entity]
   [common.teams :as teams]
   [frontend.view.grid.common.utils :as grid-utils]
   [frontend.view.grid.common.components :as components]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [reagent.core :as r]
   [frontend.view.named-view.events :as named-view-events]
   [frontend.view.not-found :as not-found]
   [frontend.view.modals.bulk-edit-entity.view :as bulk-edit-modal]
   [frontend.view.components.grid-filters.view :as grid-filters]
   [ui.components.button.view :as button]
   [ui.editors.entity-creator.view :as creator-editor]))

(defonce select-width 30)

(defn column
  [args]
  (let [{:keys [label attribute field-schema filter-data sorting-disabled? width hover-atom
                recompute-grid-sizes! flex-grow]}
        args
        default-width (-> (widths/get-column-width attribute)
                          :default-width
                          (or widths/default-width))]
    {:label               label
     :data-key            attribute
     :field-schema        field-schema
     :filter-data         filter-data
     :update-filter!      (fn [k filter-data]
                            (grid/set-filter (events/setup-args) k filter-data))
     :sort-disabled?      (boolean sorting-disabled?)
     :min-width           (-> (widths/get-column-width attribute)
                              :min-width
                              (or widths/min-width))
     :max-width           (-> (widths/get-column-width attribute)
                              :max-width
                              (or widths/max-width))
     :default-width       default-width
     :width               (or width default-width)
     :set-width           width
     :begin-width-update! (partial grid/begin-width-update! hover-atom attribute)
     :update-width!
     (partial grid/update-width! (events/setup-args) recompute-grid-sizes! hover-atom attribute)
     :end-width-update!   (partial grid/end-width-update! hover-atom attribute)
     :get-styling         (partial grid/get-styling hover-atom attribute)
     :flex-grow           flex-grow}))

(defn toolbar-actions
  [{:keys [view-mode]}]
  (let [all-teams (->> @session/all-teams
                       (remove :common/disabled?))
        account-teams (teams/resolve-account-teams @session/account all-teams)]
    [{:toolbar-button? true
      :dropdown-action? true
      :ord       2
      :hidden?   (fn []
                   (= view-mode :board-mode))
      :primary-button? false
      :add-ellipsis? true
      :on-select (fn [_e]
                   (grid/open-select-columns-curtain (events/setup-args)))
      :label     (loc/tr :action/select-columns)}
     (grid-utils/export-button {:account-teams  account-teams
                                :button-ord     3
                                :grid-state     db/fund-commitments
                                :column-keys-fn events/resolve-column-keys})
     (grid-utils/export-with-persons-button {:account-teams  account-teams
                                             :entity-type    :entity.type/fund-commitment
                                             :grid-state     db/fund-commitments
                                             :button-ord     4
                                             :column-keys-fn events/resolve-column-keys})
     (when-not (session/is-read-only-user?)
       {:toolbar-button? true
        :dropdown-action? true
        :ord       1
        :icon      :fa-plus
        :primary-button? true
        :on-select (fn [_e]
                     (f-entity/open-creator! :entity.type/fund-commitment {}))
        :label     (loc/tr :fund-commitment-add-button)})

     {:divider? true
      :dropdown-action? true
      :ord 14}

     (grid-utils/creator-config-item
      {:ord 15
       :entity-type :entity.type/fund-commitment})

     (when (= view-mode :board-mode)
       {:toolbar-button? false
        :dropdown-action? true
        :ord       16
        :add-ellipsis? true
        :label     (loc/tr :card-settings)
        :on-select (fn [_e]
                     (events/open-card-settings!))})]))

(defn toolbar-buttons-renderer
  []
  (let [setup-args (events/setup-args)
        modal-atom (:modal-state setup-args)
        open-bulk-edit! #(swap! modal-atom assoc :bulk-edit-modal true)
        selected (grid/get-selected-items setup-args)]
    [:div
     {:class "flex gap-2"}
     (if (session/is-read-only-user?)
       [:span (loc/tr :no-grid-actions-for-read-only-user)]
       [:<>
        [components/toolbar-delete-button
         {:max-delete-count   10000
          :selected-items     (grid/get-selected-items (events/setup-args))
          :on-delete-clicked! events/delete-selected-clicked}]
        [components/toolbar-bulk-edit-button
         {:max-bulk-edit-count   10000
          :selected-items        selected
          :on-bulk-edit-clicked! open-bulk-edit!}]])]))

(defn create-props-atom
  [tenant-schema _tenant selected sorted-fund-commitments filters
   grid-store recompute-grid-sizes! hover-atom modal-atom]
  (let [columns
        (vec (keep (fn [k]
                     (let [field-schema (registry/get-field-schema tenant-schema k)
                           label (attribute/attribute-label tenant-schema k)]
                       (column {:label             label
                                :attribute         k
                                :field-schema      field-schema
                                :filter-data       (get filters k)
                                :sorting-disabled? false
                                :width             (grid/get-saved-width (events/setup-args) k)
                                :hover-atom        hover-atom
                                :recompute-grid-sizes! recompute-grid-sizes!
                                :flex-grow         1})))
                   (events/resolve-column-keys)))
        editable-grid? (and (session/has-feature-access? :feature/editable-grid)
                            @session/editable-grid?)]
    (r/atom
     {:grid-store              grid-store
      :setup-args              (events/setup-args)
      :grid-entity-ids         (->> sorted-fund-commitments
                                    (mapv :db/id)
                                    (into #{}))
      :selected                selected
      :link-to-entity-field    :fund-commitment/name
      :header-height           50
      :editable-grid?          editable-grid?
      :select-width            select-width
      :min-columns-width       30
      :height                  (- (or (:height @app-db/window) 400)
                                  75) ;; TODO: lazy - fixed 120px for button row in this view
      :avail-height            (- (or (:height @app-db/window) 400)
                                  75) ;; TODO: lazy - fixed 120px for button row in this view
      :useDynamicRowHeight     false  ;; TODO: kebab-case
      :row-height              40     ;(fn [index] 40)
      :row-count               (if (sequential? sorted-fund-commitments)
                                 (count sorted-fund-commitments)
                                 0)
      :scrollToIndex           0
      :columns                 columns
      :overscan-count          1
      :no-rows-renderer        (r/partial grid/default-no-rows-render
                                          @db/fund-commitments
                                          :entity.type/fund-commitment)
      :on-row-click!           (fn [row]
                                 (let [data (when (< row (count sorted-fund-commitments))
                                              (nth sorted-fund-commitments row))]
                                   (routing/open-entity (:db/id data))))
      :on-cell-click           (fn [row col]
                                 (grid/handle-cell-click!
                                  {:columns columns
                                   :col     col
                                   :row     row
                                   :sorted-grid-entities sorted-fund-commitments
                                   :state   hover-atom}))
      :is-sorted-by?           (partial grid/is-sorted-by db/view-settings)
      :is-secondary-sorted-by? (partial grid/is-secondary-sorted-by db/view-settings)
      :sort-by!                (fn [k dir]
                                 (grid/set-sort-by (events/setup-args) k dir))
      :reorder-columns!        (fn [dragged-column after-column]
                                 (grid/reorder-columns! (events/setup-args)
                                                        dragged-column
                                                        after-column))
      :can-filter?             (fn [_k]
                                 true)
      :resizing-column         (some-> @hover-atom
                                       (get :resize-start-width)
                                       (first)
                                       (key))
      :recompute-grid-sizes!   recompute-grid-sizes!
      :all-selection!          (fn []
                                 (let [selected (:selected @db/fund-commitments)]
                                   (if (or (= :all selected) (not-empty selected))
                                     (grid/clear-selection! (events/setup-args))
                                     (swap! db/fund-commitments assoc :selected :all))))
      :select-action-renderer  toolbar-buttons-renderer
      :on-row-select!          (fn [index checked]
                                 (let [data (when (< index (count sorted-fund-commitments))
                                              (nth sorted-fund-commitments index))
                                       fund-commitment-id (:db/id data)]
                                   (if (= selected :all)
                                     (grid/select-all-but (events/setup-args) fund-commitment-id)
                                     (if (boolean checked)
                                       (swap! db/fund-commitments assoc-in
                                         [:selected fund-commitment-id]
                                         true)
                                       (swap! db/fund-commitments potpuri/dissoc-in
                                         [:selected fund-commitment-id])))))
      :deselect-entity!        (fn [entity-id]
                                 (if (= selected :all)
                                   (grid/select-all-but (events/setup-args) entity-id)
                                   (swap! db/fund-commitments
                                     potpuri/dissoc-in
                                     [:selected entity-id])))
      :row-data-getter         (r/partial grid/default-row-data-getter
                                          sorted-fund-commitments
                                          selected)

      :new-action-view         {:in-use?         true
                                :open-bulk-edit! #(swap! modal-atom assoc :bulk-edit-modal true)
                                :bulk-delete!    events/delete-selected-clicked
                                :modal-atom      modal-atom}})))

(defn render
  [grid-store _args]
  (let [tenant @session/tenant
        hover-atom (r/atom {:hover {:col nil :row nil}})
        entity-type :entity.type/fund-commitment
        recompute-grid-sizes! (fn []
                                (js/setTimeout
                                 (partial vu/recompute-grid-sizes-with-state! grid-store)
                                 0))]
    (fn [{:keys [view-mode]}]
      (let [tenant-schema @session/tenant-schema
            selected (:selected @db/fund-commitments)
            filters {}
            #_@db/fund-commitment-filters ;;TODO later
            sorted-fund-commitments (:sorted-grid-entities @db/fund-commitments)
            setup-args @(r/track events/setup-args)
            modal-atom (:modal-state setup-args)
            grid-atom (create-props-atom tenant-schema
                                         tenant
                                         selected
                                         sorted-fund-commitments
                                         filters
                                         grid-store
                                         recompute-grid-sizes!
                                         hover-atom
                                         modal-atom)
            show-quick-filters? (:show-quick-filters? @db/fund-commitments)
            card-settings? (:card-settings? @db/kanban)
            show-bulk-edit-modal? (:bulk-edit-modal @modal-atom)
            filters-props {:minimized?            (not show-quick-filters?)
                           :hide-maximize?        true
                           :universal-search?     true
                           :search-placeholder    (loc/tr :search-for-a-fund-commitment)
                           :setup-args            (events/setup-args)
                           :set-universal-search! (r/partial grid/set-universal-search! setup-args)
                           :set-name-search!      (r/partial grid/set-name-search! setup-args)
                           :hide-named-views?     true
                           :named-views           (events/named-views)
                           :current-view          @db/view-settings
                           :columns               (events/resolve-column-keys)
                           :all-columns           (events/resolve-column-key-universe)
                           :get-column-label      (partial attribute/attribute-label @session/tenant-schema)
                           :set-filter!           (fn [k filter-data]
                                                    (grid/set-filter (events/setup-args) k filter-data))
                           :edit-filter!          (fn [k filter-data]
                                                    (grid/open-filter-curtain
                                                     {:field-key      k
                                                      :field-schema   (registry/get-field-schema tenant-schema
                                                                                                 k)
                                                      :options        (->>
                                                                       (events/resolve-column-key-universe)
                                                                       (map (juxt identity
                                                                                  (partial
                                                                                   registry/get-field-schema
                                                                                   tenant-schema))))
                                                      :current-filter filter-data
                                                      :update-fn!     (fn [k filter-data]
                                                                        (grid/set-filter (events/setup-args)
                                                                                         k
                                                                                         filter-data))}
                                                     nil))
                           :hide-qf-attributes    []
                           :load-view!            (fn [named-view]
                                                    (events/load-view! named-view)
                                                    (grid/clear-selection! {:state db/fund-commitments})
                                                    (recompute-grid-sizes!))
                           :save-view!            events/save-current-view!
                           :delete-view!          named-view-events/delete-view!
                           :toggle-minimize!      (fn []
                                                    (events/toggle-quick-filters!)
                                                    (.setTimeout js/window recompute-grid-sizes! 1000))}
            hiccup
            (if card-settings?
              [kanban/card-settings-view (events/setup-args)]
              [:<>
               [grid-filters/view
                filters-props]
               [creator-editor/modal-view]
               [:div
                {:class "flex flex-auto ps-4"}
                [:div
                 {:class-name (fu/cs "grid-view-content"
                                     (when-not show-quick-filters? "named-views-minimized")
                                     (when (= view-mode :board-mode) "board-view"))}

                 (when show-bulk-edit-modal?
                   [bulk-edit-modal/view
                    {:setup-args   setup-args
                     :entity-type  entity-type
                     :close-modal! #(swap! modal-atom dissoc :bulk-edit-modal)}])

                 (if (= view-mode :board-mode)
                   [kanban/view
                    {:setup-args   (events/setup-args)
                     :card-actions [(kanban/open-entity-action
                                     {:entity-type :entity.type/fund-commitment})]}]
                   [scrollable-grid/view
                    {:props-atom grid-atom
                     :hover-atom hover-atom}])]]])]
        hiccup))))

(defn toolbar
  [args]
  (let [view-mode (or (:view-mode @db/view-settings)
                      (:view-mode (ls/get-in (events/ls-path)))
                      (:initial-mode args)
                      :grid-mode)
        board-mode? (= view-mode :board-mode)
        card-mode (or (:card-mode @db/kanban)
                      :short-card)
        has-empty-groups? (when board-mode?
                            (kanban/has-empty-groups? @(r/track events/setup-args)))]
    [:div
     {:class "px-4 py-1 flex items-center justify-between
              bg-base-light
              border-b border-solid border-base-light-border-paler"}
     [:div
      {:class "w-full flex justify-between gap-6"}
      [:div
       [:div
        {:class "flex gap-2 items-center"}
        [:div
         {:class "flex items-center gap-1"}
         [:label
          {:class "font-semibold"}
          (loc/tr :fund-commitments-grid)]

        [:span
         {:class "h-5 px-2 flex items-center justify-center text-xs font-semibold text-base-light-text bg-complement-regular rounded-full"}
         (count @db/sorted-kanban-entities)]]

        [grid-components/named-view-selector
         {:named-views        (events/named-views)
          :current-view       @db/view-settings
          :load-view!         (fn [id]
                                (events/load-view! (events/named-view-by-id id))
                                (grid/clear-selection! {:state db/fund-commitments}))
          :delete-view!       named-view-events/start-delete-view!
          :accounts-with-view-access (session/active-accounts-with-module-access
                                      :module/fundraising)
          :save-current-view! events/save-current-view!}]

        (when board-mode?
          [grid-components/sort-selector
           {:view-settings   @db/view-settings
            :entity-type     :entity.type/fund-commitment
            :sortable-fields (events/resolve-column-key-universe)
            :set-sort-by!    (fn [k dir]
                               (grid/set-sort-by (events/setup-args) k dir))}])

        (when board-mode?
          [grid-components/grouped-by-selector
           {:view-settings      @db/view-settings
            :entity-type        :entity.type/fund-commitment
            :default-grouped-by :fund-commitment/owner
            :grouped-by-fields  (->> (events/resolve-column-key-universe)
                                     (filter grid/groupable-attribute?))
            :set-grouped-by!    (fn [k]
                                  (swap! db/view-settings assoc :grouped-by k)
                                  (ls/update-in (events/ls-path)
                                                assoc
                                                :grouped-by
                                                k))}])]]
      [:div
       {:class "flex justify-content-end items-center gap-2"}
       ;(when-not board-mode?
       ;  [grid-components/editable-grid-toggle])
       (when (and board-mode? (not= card-mode :short-card))
         [button/square-secondary-open
          {:on-click (fn [_] (events/collapse-all!))
           :tooltip  (loc/tr :collapse-all)}
          [:i.fal.fa-compress-alt]])
       (when (and board-mode? (= card-mode :short-card))
         [button/square-secondary-open
          {:tooltip (loc/tr :expand-all)
           :on-click   (fn [_] (events/expand-all!))}
          [:i.fal.fa-expand-alt]])

       [button/square-secondary-open
        {:tooltip (grid-components/view-mode-tooltip board-mode?)
         :on-click   (fn [_]
                       (events/switch-mode! view-mode))}
        (if board-mode?
          [:i.fal.fa-table]
          [:i.fal.fa-signal-3.fa-rotate-180.kanban-icon])]

       (when (and board-mode? has-empty-groups?)
         [grid-utils/hide-empty-kanban-groups-toggle "fund-commitments-grid-toolbar" db/kanban])

       [:div.toolbar-dropdown
        (zap-toolbar/dropdown (->> (toolbar-actions {:view-mode view-mode})
                                   (filter :dropdown-action?)
                                   (remove (fn [{:keys [hidden?]}]
                                             (when (fn? hidden?)
                                               (hidden?))))
                                   (sort-by :ord))
                              true)
       ]]]
    ]))

(defn view
  [_args]
  (let [grid-store (atom {})
        close-atom (atom false)
        resizer (fn []
                  (js/setTimeout (partial vu/recompute-grid-sizes-with-state! grid-store) 100))]
    (.addEventListener js/window "resize" resizer)
    (r/create-class
     {:component-did-mount    (fn []
                                (fu/timeout 10 (partial events/setup close-atom)))
      :component-will-unmount (fn []
                                (reset! close-atom true)
                                (events/cleanup)
                                (.removeEventListener js/window "resize" resizer))
      :reagent-render         (partial render grid-store)})))

(defmethod routing/render-view :fund-commitments-grid
  [{:keys [_route-params _view-params]}]
  (let [has-view-access? (or (session/has-funds-access?)
                             (session/fundraising?))]
    [default-page-layout/view
     (cond
       (not has-view-access?)
       [not-found/not-found-content]

       (session/new-grid-2023?)
       [:<>
        [fund-commitment-listing/page-content]]

       :else
       (let [view-mode (or (:view-mode @db/view-settings)
                           (:view-mode (ls/get-in (events/ls-path)))
                           :grid-mode)]
         [:<>
          [components/card-settings-toolbar
           [toolbar {:view-mode view-mode}] db/kanban events/close-card-settings!]
          [view {:view-mode view-mode}]]))]))
